// src/services/userManagementService.js
import { supabase } from '../lib/supabase';

// Helper function to convert snake_case to camelCase
const toCamelCase = (obj) => {
  if (Array.isArray(obj)) {
    return obj?.map(toCamelCase);
  } else if (obj !== null && obj?.constructor === Object) {
    return Object.keys(obj)?.reduce((result, key) => {
      const camelKey = key?.replace(/_([a-z])/g, (g) => g?.[1]?.toUpperCase());
      result[camelKey] = toCamelCase(obj?.[key]);
      return result;
    }, {});
  }
  return obj;
};

// Helper function to convert camelCase to snake_case
const toSnakeCase = (obj) => {
  if (Array.isArray(obj)) {
    return obj?.map(toSnakeCase);
  } else if (obj !== null && obj?.constructor === Object) {
    return Object.keys(obj)?.reduce((result, key) => {
      const snakeKey = key?.replace(/[A-Z]/g, (letter) => `_${letter?.toLowerCase()}`);
      result[snakeKey] = toSnakeCase(obj?.[key]);
      return result;
    }, {});
  }
  return obj;
};

// ============================================
// USER PROFILES SERVICE
// ============================================

export const userProfilesService = {
  // Get all user profiles
  async getAll(filters = {}) {
    try {
      let query = supabase?.from('user_profiles')?.select('*')?.order('created_at', { ascending: false });

      if (filters?.role) {
        query = query?.eq('role', filters?.role);
      }

      if (filters?.status) {
        query = query?.eq('status', filters?.status);
      }

      if (filters?.department) {
        query = query?.eq('department', filters?.department);
      }

      const { data, error } = await query;

      if (error) throw error;
      return { data: toCamelCase(data), error: null };
    } catch (error) {
      console.error('Error fetching user profiles:', error);
      return { data: null, error };
    }
  },

  // Get user profile by ID
  async getById(id) {
    try {
      const { data, error } = await supabase?.from('user_profiles')?.select('*')?.eq('id', id)?.single();

      if (error) throw error;
      return { data: toCamelCase(data), error: null };
    } catch (error) {
      console.error('Error fetching user profile:', error);
      return { data: null, error };
    }
  },

  // Get user statistics
  async getStatistics() {
    try {
      const { data, error } = await supabase?.from('user_profiles')?.select('role, status');

      if (error) throw error;

      const stats = {
        totalUsers: data?.length || 0,
        activeUsers: data?.filter(u => u?.status === 'active')?.length || 0,
        adminCount: data?.filter(u => u?.role === 'admin')?.length || 0,
        analystCount: data?.filter(u => u?.role === 'analyst')?.length || 0,
        merchantCount: data?.filter(u => u?.role === 'merchant')?.length || 0,
        inactiveUsers: data?.filter(u => u?.status === 'inactive')?.length || 0,
        suspendedUsers: data?.filter(u => u?.status === 'suspended')?.length || 0
      };

      return { data: stats, error: null };
    } catch (error) {
      console.error('Error fetching user statistics:', error);
      return { data: null, error };
    }
  },

  // Update user profile
  async update(id, userData) {
    try {
      const updateData = toSnakeCase(userData);
      updateData.updated_at = new Date()?.toISOString();

      const { data, error } = await supabase?.from('user_profiles')?.update(updateData)?.eq('id', id)?.select()?.single();

      if (error) throw error;
      return { data: toCamelCase(data), error: null };
    } catch (error) {
      console.error('Error updating user profile:', error);
      return { data: null, error };
    }
  },

  // Update user role
  async updateRole(id, role) {
    try {
      const { data, error } = await supabase?.from('user_profiles')?.update({ role, updated_at: new Date()?.toISOString() })?.eq('id', id)?.select()?.single();

      if (error) throw error;
      return { data: toCamelCase(data), error: null };
    } catch (error) {
      console.error('Error updating user role:', error);
      return { data: null, error };
    }
  },

  // Update user status
  async updateStatus(id, status) {
    try {
      const { data, error } = await supabase?.from('user_profiles')?.update({ status, updated_at: new Date()?.toISOString() })?.eq('id', id)?.select()?.single();

      if (error) throw error;
      return { data: toCamelCase(data), error: null };
    } catch (error) {
      console.error('Error updating user status:', error);
      return { data: null, error };
    }
  },

  // Subscribe to user profile changes
  subscribe(callback) {
    const channel = supabase?.channel('user_profiles_changes')?.on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'user_profiles'
        },
        (payload) => {
          callback(toCamelCase(payload));
        }
      )?.subscribe();

    return () => supabase?.removeChannel(channel);
  }
};

// ============================================
// PERMISSIONS SERVICE
// ============================================

export const permissionsService = {
  // Get all permissions
  async getAll(filters = {}) {
    try {
      let query = supabase?.from('permissions')?.select('*')?.order('role', { ascending: true });

      if (filters?.role) {
        query = query?.eq('role', filters?.role);
      }

      const { data, error } = await query;

      if (error) throw error;
      return { data: toCamelCase(data), error: null };
    } catch (error) {
      console.error('Error fetching permissions:', error);
      return { data: null, error };
    }
  },

  // Get permissions by role
  async getByRole(role) {
    try {
      const { data, error } = await supabase?.from('permissions')?.select('*')?.eq('role', role);

      if (error) throw error;
      return { data: toCamelCase(data), error: null };
    } catch (error) {
      console.error('Error fetching permissions by role:', error);
      return { data: null, error };
    }
  },

  // Update permission
  async update(id, permissionData) {
    try {
      const updateData = toSnakeCase(permissionData);
      updateData.updated_at = new Date()?.toISOString();

      const { data, error } = await supabase?.from('permissions')?.update(updateData)?.eq('id', id)?.select()?.single();

      if (error) throw error;
      return { data: toCamelCase(data), error: null };
    } catch (error) {
      console.error('Error updating permission:', error);
      return { data: null, error };
    }
  },

  // Bulk update permissions for a role
  async bulkUpdateByRole(role, updates) {
    try {
      const promises = updates?.map(update => 
        supabase?.from('permissions')?.update(toSnakeCase(update))?.eq('role', role)?.eq('feature_category', update?.featureCategory)?.select()?.single()
      );

      const results = await Promise.all(promises);
      const errors = results?.filter(r => r?.error);

      if (errors?.length > 0) {
        throw errors?.[0]?.error;
      }

      return { data: toCamelCase(results?.map(r => r?.data)), error: null };
    } catch (error) {
      console.error('Error bulk updating permissions:', error);
      return { data: null, error };
    }
  }
};

// ============================================
// AUDIT LOGS SERVICE
// ============================================

export const auditLogsService = {
  // Get all audit logs
  async getAll(filters = {}) {
    try {
      let query = supabase?.from('audit_logs')?.select('*, user_profiles!audit_logs_user_id_fkey(full_name, email, role)')?.order('timestamp', { ascending: false });

      if (filters?.userId) {
        query = query?.eq('user_id', filters?.userId);
      }

      if (filters?.action) {
        query = query?.eq('action', filters?.action);
      }

      if (filters?.limit) {
        query = query?.limit(filters?.limit);
      }

      const { data, error } = await query;

      if (error) throw error;
      return { data: toCamelCase(data), error: null };
    } catch (error) {
      console.error('Error fetching audit logs:', error);
      return { data: null, error };
    }
  },

  // Create audit log
  async create(logData) {
    try {
      const { data, error } = await supabase?.from('audit_logs')?.insert(toSnakeCase(logData))?.select()?.single();

      if (error) throw error;
      return { data: toCamelCase(data), error: null };
    } catch (error) {
      console.error('Error creating audit log:', error);
      return { data: null, error };
    }
  },

  // Get recent activity
  async getRecentActivity(limit = 20) {
    try {
      const { data, error } = await supabase?.from('audit_logs')?.select('*, user_profiles!audit_logs_user_id_fkey(full_name, email, role)')?.order('timestamp', { ascending: false })?.limit(limit);

      if (error) throw error;
      return { data: toCamelCase(data), error: null };
    } catch (error) {
      console.error('Error fetching recent activity:', error);
      return { data: null, error };
    }
  },

  // Subscribe to audit log changes
  subscribe(callback) {
    const channel = supabase?.channel('audit_logs_changes')?.on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'audit_logs'
        },
        (payload) => {
          callback(toCamelCase(payload));
        }
      )?.subscribe();

    return () => supabase?.removeChannel(channel);
  }
};